import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { clearPanelToken, readPanelToken, storePanelToken } from '../lib/panelSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);
const SIGN_IN_ROLES = ['user', 'reseller', 'admin', 'staff'];
const canceledSignIn = () => Object.assign(new Error('Sign-in was canceled.'), { code: 'AUTH_CANCELLED' });

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(readPanelToken);
  const [loading, setLoading] = useState(true);
  // null = not loaded yet; [] = staff with no permissions
  const [permissions, setPermissions] = useState(null);
  const [sessionReset, setSessionReset] = useState(0);
  const authGeneration = useRef(0);
  const authRequest = useRef(null);

  const cancelLogin = useCallback(() => {
    authGeneration.current += 1;
    authRequest.current?.abort();
    authRequest.current = null;
  }, []);

  useEffect(() => () => cancelLogin(), [cancelLogin]);

  const logout = useCallback(() => {
    cancelLogin();
    clearPanelToken();
    setToken(null);
    setUser(null);
    setPermissions(null);
    setLoading(false);
    setSessionReset(value => value + 1);
  }, [cancelLogin]);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
    });

    // Update headers when token changes
    if (token) {
      instance.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete instance.defaults.headers.Authorization;
    }

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && readPanelToken() === token) {
          logout();
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [token, logout]);

  const fetchUser = useCallback(async (signal) => {
    const isCurrent = () => !signal?.aborted && readPanelToken() === token;
    try {
      const response = await api.get('/auth/me', { signal });
      if (isCurrent()) setUser(response.data);
      return response.data;
    } catch (error) {
      if (isCurrent()) {
        console.error('Failed to fetch user');
        logout();
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [api, logout, token]);

  useEffect(() => {
    const controller = new AbortController();
    if (token) {
      setLoading(true);
      fetchUser(controller.signal);
    } else {
      setLoading(false);
    }
    return () => controller.abort();
  }, [token, fetchUser]);

  // Do not allow a previous account's late permissions response to affect
  // the new session after login, logout, or an administrator handoff.
  useEffect(() => {
    const controller = new AbortController();
    setPermissions(null);
    if (user && (user.role === 'admin' || user.role === 'staff')) {
      api.get('/admin/my-permissions', { signal: controller.signal })
        .then((res) => {
          if (!controller.signal.aborted) setPermissions(res.data.permissions || []);
        })
        .catch(() => {
          if (!controller.signal.aborted) setPermissions([]);
        });
    }
    return () => controller.abort();
  }, [user, api]);

  const signIn = useCallback(async (path, payload, allowedRoles = SIGN_IN_ROLES, rememberMe = true) => {
    cancelLogin();
    const generation = authGeneration.current;
    const controller = new AbortController();
    authRequest.current = controller;
    const current = () => generation === authGeneration.current && !controller.signal.aborted;
    try {
      const response = await axios.post(`${API}${path}`, payload, { signal: controller.signal });
      if (!current()) throw canceledSignIn();
      const { access_token, token_type } = response.data || {};
      if (path === '/auth/login' && token_type === '2fa_pending') return { requires2FA: true };
      if (typeof access_token !== 'string' || !access_token.trim() || token_type === '2fa_pending') throw new Error('The server did not return a valid session.');
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }, signal: controller.signal,
      });
      if (!current()) throw canceledSignIn();
      if (!SIGN_IN_ROLES.includes(userResponse.data?.role) || !allowedRoles.includes(userResponse.data.role)) {
        throw new Error('This account cannot sign in through this portal.');
      }
      // Authenticate and authorize before persisting anything. Failed /me,
      // logout, a canceled prompt, and a newer sign-in must never revive a token.
      storePanelToken(access_token, rememberMe);
      setPermissions(null); setUser(userResponse.data); setToken(access_token);
      return { success: true, user: userResponse.data, recoveryCodesRemaining: response.data.recovery_codes_remaining };
    } catch (error) {
      if (!current()) throw canceledSignIn();
      throw error;
    } finally {
      if (authRequest.current === controller) authRequest.current = null;
    }
  }, [cancelLogin]);

  const login = useCallback((username, password, options = {}) => {
    const { allowedRoles, rememberMe = true } = options;
    return signIn('/auth/login', { username, password, remember_me: rememberMe }, allowedRoles, rememberMe);
  }, [signIn]);
  const login2FA = useCallback((username, password, code, options = {}) => {
    const { allowedRoles, rememberMe = true } = options;
    return signIn('/auth/login/2fa', { username, password, code, remember_me: rememberMe }, allowedRoles, rememberMe);
  }, [signIn]);
  const loginRecovery = useCallback((username, password, recoveryCode, options = {}) => {
    const { allowedRoles = ['user', 'reseller'], rememberMe = true } = options;
    return signIn('/auth/2fa/recovery/verify', { username, password, recovery_code: recoveryCode, remember_me: rememberMe }, allowedRoles, rememberMe);
  }, [signIn]);

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    if (response.data.verification_required) return response.data;
    throw new Error('Registration did not return a verification result.');
  };

  const acceptExternalToken = useCallback(async (accessToken) => {
    // An owner handoff may arrive while a staff session is still active.
    // Clear that identity first so the UI can never render stale, limited
    // permissions while the administrator token is being verified.
    cancelLogin();
    const generation = authGeneration.current;
    const controller = new AbortController();
    authRequest.current = controller;
    const current = () => generation === authGeneration.current && !controller.signal.aborted;
    setLoading(true);
    setUser(null);
    setPermissions(null);

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }, signal: controller.signal,
      });
      if (!current()) throw canceledSignIn();
      if (response.data?.role !== 'admin') {
        throw new Error('Owner handoff did not resolve to an administrator');
      }

      storePanelToken(accessToken);
      setUser(response.data);
      setToken(accessToken);
      return response.data;
    } catch (error) {
      if (!current()) throw canceledSignIn();
      clearPanelToken();
      setToken(null);
      setUser(null);
      throw error;
    } finally {
      if (authRequest.current === controller) authRequest.current = null;
      if (current()) setLoading(false);
    }
  }, [cancelLogin]);

  const refreshUser = useCallback(() => fetchUser(), [fetchUser]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      api,
      login, 
      login2FA,
      loginRecovery,
      cancelLogin,
      sessionReset,
      register, 
      acceptExternalToken,
      logout,
      refreshUser,
      isAdmin: user?.role === 'admin',
      isStaff: user?.role === 'staff' || user?.role === 'admin',
      permissions,
      permissionsLoaded: user?.role === 'admin' || permissions !== null,
      // Mirrors the backend rbac._holds rule. `needed` may be a module name
      // ("dashboard") or a specific id ("orders.cancel_refund"). my-permissions
      // returns granular ids, so a plain .includes() would fail every
      // module-level nav/route check — the "No access" regression.
      hasPermission: (needed) => {
        if (user?.role === 'admin') return true;
        if (user?.role !== 'staff') return false;
        const held = permissions || [];
        const module = String(needed).split('.')[0];
        if (!String(needed).includes('.')) {
          // module-level: bare module OR any module.* grant
          return held.includes(module) || held.some((h) => h.split('.')[0] === module);
        }
        // specific: exact id OR a legacy bare-module grant
        return held.includes(needed) || held.includes(module);
      },
    }}>
      {children}
    </AuthContext.Provider>
  );
};

